// The Doctor × Month matrix table. One sticky "Global" row at the top
// (company-wide closures / imported public holidays) followed by one row per
// doctor. Every month cell is a clickable mini month-strip.
//
// Mirrors `slots-heatmap/heatmap-matrix.jsx` but on a different axis: slots are
// Doctor × Weekday with intra-day time bars; time off is Doctor × Month with
// date-span bars.

import { useMemo } from "react";
import { PartyPopper } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { HueAvatar } from "../slots-heatmap/heatmap-bits";
import { MonthCell } from "./month-cell";
import {
  MONTH_SHORT,
  entryIntersectsMonth,
  yearMonths,
} from "./time-off-utils";

const GLOBAL_KEY = "__global__";

// The 12 month cells + the per-lane total for one row (Global or a doctor).
// `rows` is the group's entries; `keyId` is the doctorId or GLOBAL_KEY (used
// as `activeCell.doctorId`).
function MonthLaneCells({ keyId, rows, months, activeCell, onSelectCell }) {
  // Bucket the lane's rows by month index once, instead of doing 12 filters
  // per render. A multi-month vacation lands in every month it overlaps.
  const { byMonth, total } = useMemo(() => {
    const buckets = new Map();
    let sum = 0;
    for (const row of rows) {
      for (let monthIdx = 0; monthIdx < months.length; monthIdx += 1) {
        if (!entryIntersectsMonth(row, months[monthIdx])) continue;
        if (!buckets.has(monthIdx)) buckets.set(monthIdx, []);
        buckets.get(monthIdx).push(row);
        sum += 1;
      }
    }
    return { byMonth: buckets, total: sum };
  }, [rows, months]);

  return (
    <>
      {months.map((monthStart, idx) => {
        const monthRows = byMonth.get(idx) || [];
        const month = idx + 1; // 1..12 matches Luxon's `month` accessor
        const isActive =
          activeCell?.doctorId === keyId && activeCell?.month === month;
        return (
          <td key={idx} className="border-b border-border p-0 align-top">
            <button
              type="button"
              onClick={() => onSelectCell({ doctorId: keyId, month })}
              className={cn(
                "group relative block h-[78px] w-full p-1 text-left transition-colors",
                isActive ? "bg-primary/10" : "hover:bg-muted/40",
              )}
            >
              <MonthCell rows={monthRows} monthStart={monthStart} />
              {isActive ? (
                <span className="pointer-events-none absolute inset-0 rounded-[2px] ring-2 ring-inset ring-primary/70" />
              ) : null}
            </button>
          </td>
        );
      })}
      <td className="border-b border-border px-2 align-middle">
        <div className="flex items-center justify-end gap-1.5">
          <span className="font-mono text-[10px] tabular-nums text-foreground/80">
            {total}×
          </span>
        </div>
      </td>
    </>
  );
}

// The sticky Global row at the top — public holidays / company-wide closures.
// Uses a PartyPopper icon + a soft rose tint to telegraph "company-wide".
function GlobalRow({ group, months, activeCell, onSelectCell }) {
  return (
    <tr>
      <td className="border-b border-border bg-rose-50/40 px-4 py-2 align-top dark:bg-rose-950/15">
        <div className="flex items-center gap-2">
          <span className="inline-grid size-[26px] shrink-0 place-items-center rounded-full bg-rose-500/15 text-rose-600 dark:bg-rose-500/20 dark:text-rose-300">
            <PartyPopper className="size-3.5" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="truncate text-xs font-semibold text-foreground">
              {group?.label || "Global"}
            </div>
            <div className="text-[10px] tabular-nums text-muted-foreground/60">
              Company-wide · {group?.rows?.length ?? 0} entries
            </div>
          </div>
        </div>
      </td>
      <MonthLaneCells
        keyId={GLOBAL_KEY}
        rows={group?.rows || []}
        months={months}
        activeCell={activeCell}
        onSelectCell={onSelectCell}
      />
    </tr>
  );
}

// One doctor row. Time off is single-lane per doctor (no tenant rowSpan like
// in slots — time off rows aren't tenant-scoped).
function DoctorRow({ group, months, activeCell, onSelectCell }) {
  return (
    <tr>
      <td className="border-b border-border bg-muted/30 px-4 py-2 align-top">
        <div className="flex items-center gap-2">
          <HueAvatar name={group.label} id={group.key} size={26} />
          <div className="min-w-0">
            <div className="truncate text-xs font-semibold text-foreground">
              {group.label}
            </div>
            <div className="text-[10px] tabular-nums text-muted-foreground/60">
              {group.rows.length} entries
            </div>
          </div>
        </div>
      </td>
      <MonthLaneCells
        keyId={group.key}
        rows={group.rows}
        months={months}
        activeCell={activeCell}
        onSelectCell={onSelectCell}
      />
    </tr>
  );
}

export function TimeOffHeatmapMatrix({
  groups = [],
  year,
  activeCell,
  onSelectCell,
  isLoading,
}) {
  const months = useMemo(() => yearMonths(year), [year]);

  // The data hook already places `__global__` first when `groupBy === "doctor"`
  // (use-time-off-list.js sorts globals before doctors). Pull it out to render
  // as the special sticky row.
  const { globalGroup, doctorGroups } = useMemo(() => {
    let g = null;
    const docs = [];
    for (const group of groups) {
      if (group.key === GLOBAL_KEY) g = group;
      else docs.push(group);
    }
    return { globalGroup: g, doctorGroups: docs };
  }, [groups]);

  const isEmpty = !globalGroup && doctorGroups.length === 0;

  return (
    <div className="relative min-h-0 flex-1">
      <ScrollArea className="h-full">
        <table className="w-full border-separate border-spacing-0 text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              <th className="w-[220px] border-b border-border bg-card/60 px-4 py-2 text-left backdrop-blur">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground/60">
                  Doctor
                </div>
              </th>
              {MONTH_SHORT.map((label, idx) => (
                <th
                  key={idx}
                  className="border-b border-border bg-card/60 px-2 py-2 backdrop-blur"
                >
                  <div className="flex flex-col items-center">
                    <span className="text-[11px] font-bold text-foreground">
                      {label}
                    </span>
                    <span className="text-[10px] tabular-nums text-muted-foreground">
                      {months[idx]?.toFormat?.("yyyy")?.slice(2) ?? ""}
                    </span>
                  </div>
                </th>
              ))}
              <th className="w-[64px] border-b border-border bg-card/60 px-2 py-2 text-right backdrop-blur">
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground/60">
                  Total
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {globalGroup ? (
              <GlobalRow
                group={globalGroup}
                months={months}
                activeCell={activeCell}
                onSelectCell={onSelectCell}
              />
            ) : null}
            {doctorGroups.map((group) => (
              <DoctorRow
                key={group.key}
                group={group}
                months={months}
                activeCell={activeCell}
                onSelectCell={onSelectCell}
              />
            ))}
          </tbody>
        </table>
      </ScrollArea>

      {isLoading && isEmpty ? (
        <div className="absolute inset-0 grid place-items-center bg-background/60">
          <Spinner className="size-6" />
        </div>
      ) : null}
      {!isLoading && isEmpty ? (
        <div className="absolute inset-0 grid place-items-center p-6">
          <div className="rounded-lg border border-dashed border-border bg-muted/15 p-6 text-center">
            <p className="text-sm font-medium text-foreground">
              No time off in range
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Try widening the year, importing public holidays, or creating a
              new block.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
